import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { type AnnounceMode, liveAnnouncerStore } from './liveAnnouncerStore';

export interface LiveAnnouncerProps {
  /**
   * Render the live regions in a portal on `document.body` (default) so they
   * live outside the app's DOM subtree and stay mounted regardless of routing.
   * Set to `false` to render inline instead.
   */
  portal?: boolean;
}

const srOnlyStyle: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: 0,
};

/**
 * A persistent pair of ARIA live regions (`polite` + `assertive`) that mirror
 * announcements published via `announce()`.
 *
 * Mount this **once**, at the very root of the application (outside the app /
 * router tree) so the live regions already exist in the DOM before any content
 * renders — a prerequisite for screen readers to reliably pick up updates.
 *
 * @example
 * ```tsx
 * createRoot(el).render(
 *   <>
 *     <LiveAnnouncer />
 *     <App />
 *   </>,
 * );
 * ```
 */
export const LiveAnnouncer = ({ portal = true }: LiveAnnouncerProps) => {
  const [politeMessage, setPoliteMessage] = useState('');
  const [assertiveMessage, setAssertiveMessage] = useState('');
  const timeoutRefs = useRef<
    Record<AnnounceMode, ReturnType<typeof setTimeout> | undefined>
  >({ polite: undefined, assertive: undefined });

  useEffect(() => {
    const timeouts = timeoutRefs.current;

    const setMessageForMode = (mode: AnnounceMode, value: string) => {
      if (mode === 'assertive') {
        setAssertiveMessage(value);
      } else {
        setPoliteMessage(value);
      }
    };

    const unsubscribe = liveAnnouncerStore.subscribe(({ message, mode }) => {
      // Clear first so identical consecutive messages are still announced,
      // then set on the next tick to register a DOM change. Each mode keeps its
      // own pending timeout so a polite and an assertive announcement in quick
      // succession don't cancel each other.
      setMessageForMode(mode, '');
      clearTimeout(timeouts[mode]);
      timeouts[mode] = setTimeout(() => setMessageForMode(mode, message), 100);
    });

    return () => {
      unsubscribe();
      clearTimeout(timeouts.polite);
      clearTimeout(timeouts.assertive);
    };
  }, []);

  const regions = (
    <>
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={srOnlyStyle}
      >
        {politeMessage}
      </div>
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        style={srOnlyStyle}
      >
        {assertiveMessage}
      </div>
    </>
  );

  if (portal && typeof document !== 'undefined') {
    return createPortal(regions, document.body);
  }

  return regions;
};
